import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Rating from '~/components/Rating'

const RatingPage: Component = () => {
  const [producto, setProducto] = createSignal(4)
  const [precision, setPrecision] = createSignal(3.5)

  return (
    <>
      <Title>Rating - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Rating</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Calificación por estrellas con soporte de medias estrellas, vista previa al pasar el cursor, contador de valor y todos los colores y tamaños del tema.
          </p>
        </div>

        <div class="space-y-12">
          {/* Interacción */}
          <Card>
            <Card.Header
              title="Calificación de producto"
              subtitle="Controlado con señal y contador de valor. Pasa el cursor y haz clic para calificar."
              icon={<Icon icon="material-symbols-light:star-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 p-10 bg-surface-muted rounded-3xl border border-border border-dashed">
                <div class="space-y-6">
                  <h4 class="text-xs font-semibold text-foreground-subtle pl-3 border-l-4 border-warning">Tu calificación</h4>
                  <Rating value={producto()} onChange={setProducto} showValue size="lg" />
                  <p class="text-sm text-foreground-subtle">
                    Valor actual: <span class="font-mono text-foreground">{producto()}</span>
                  </p>
                </div>
                <div class="space-y-6">
                  <h4 class="text-xs font-semibold text-foreground-subtle pl-3 border-l-4 border-primary">Con medias estrellas</h4>
                  <Rating precision={0.5} value={precision()} onChange={setPrecision} showValue size="lg" />
                  <p class="text-sm text-foreground-subtle">
                    Valor actual: <span class="font-mono text-foreground">{precision()}</span>
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Matriz de color */}
          <Card>
            <Card.Header
              title="Matriz de color"
              subtitle="Las estrellas llenas usan el color sólido de cada variante del tema."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-8 p-10">
                {(['primary', 'success', 'danger', 'warning', 'info', 'secondary'] as const).map(c => (
                  <div class="space-y-2">
                    <Rating value={5} readOnly color={c} />
                    <Badge variant="soft" color={c}>
                      {c}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>

          {/* Tamaños */}
          <Card>
            <Card.Header
              title="Tamaños"
              subtitle="La escala completa xs → xl, con la misma proporción que el resto de la librería."
              icon={<Icon icon="material-symbols-light:format-size" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-wrap items-center gap-x-12 gap-y-6 p-10">
                {(['xs', 'sm', 'md', 'lg', 'xl'] as const).map(size => (
                  <Rating value={4} readOnly size={size} />
                ))}
              </div>
            </Card.Body>
          </Card>

          {/* Estados */}
          <Card>
            <Card.Header
              title="Estados"
              subtitle="Solo lectura, deshabilitado, sin valor y con icono personalizado."
              icon={<Icon icon="material-symbols-light:info-outline" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="space-y-8 p-10">
                <div class="flex flex-wrap items-center gap-x-12 gap-y-6">
                  <Rating value={4.5} precision={0.5} readOnly />
                  <span class="text-sm text-foreground-subtle">readOnly (4.5)</span>
                </div>
                <div class="flex flex-wrap items-center gap-x-12 gap-y-6">
                  <Rating value={3} disabled />
                  <span class="text-sm text-foreground-subtle">disabled</span>
                </div>
                <div class="flex flex-wrap items-center gap-x-12 gap-y-6">
                  <Rating value={0} />
                  <span class="text-sm text-foreground-subtle">sin valor</span>
                </div>
                <div class="flex flex-wrap items-center gap-x-12 gap-y-6">
                  <Rating value={2} icon="material-symbols-light:favorite" emptyIcon="material-symbols-light:favorite-outline" color="danger" />
                  <span class="text-sm text-foreground-subtle">icono personalizado</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente Rating."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">value</td>
                      <td class="px-6 py-4 italic text-xs">number</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Valor controlado.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">defaultValue</td>
                      <td class="px-6 py-4 italic text-xs">number</td>
                      <td class="px-6 py-4 font-mono">0</td>
                      <td class="px-6 py-4 italic">Valor inicial en modo no controlado.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">max</td>
                      <td class="px-6 py-4 italic text-xs">number</td>
                      <td class="px-6 py-4 font-mono">5</td>
                      <td class="px-6 py-4 italic">Número de estrellas.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">precision</td>
                      <td class="px-6 py-4 italic text-xs">1 | 0.5</td>
                      <td class="px-6 py-4 font-mono">1</td>
                      <td class="px-6 py-4 italic">Granularidad del valor: entero o media estrella.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">size</td>
                      <td class="px-6 py-4 italic text-xs">xs | sm | md | lg | xl</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4 italic">Escala física del componente.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">color</td>
                      <td class="px-6 py-4 italic text-xs">primary | secondary | success | danger | warning | info | default</td>
                      <td class="px-6 py-4 font-mono">'warning'</td>
                      <td class="px-6 py-4 italic">Color de las estrellas llenas (default/warning = ámbar).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">readOnly</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Solo visualización, sin interacción.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">disabled</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Deshabilita la interacción.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">icon / emptyIcon</td>
                      <td class="px-6 py-4 italic text-xs">string (Iconify)</td>
                      <td class="px-6 py-4 font-mono">star / star-outline</td>
                      <td class="px-6 py-4 italic">Iconos de estrella llena y vacía.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">showValue</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Muestra el contador (ej: 4/5) al lado.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">name</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Input oculto para integrarse con Form/useForm.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">onChange</td>
                      <td class="px-6 py-4 italic text-xs">(value: number) =&gt; void</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Se llama al cambiar el valor.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default RatingPage
